<script>

export default {
  name: 'PrivacyPolicy',

}
</script>

<template>
  <div class="privacy-page-container">
    <div class="privacy-container">
      <h1 class="title">Privacy Policy</h1>
      <p class="last-updated"><strong>Effective Date:</strong> 01/01/2025</p>
      
      <div class="privacy-content">
        <p class="intro-text">Welcome to CrowdHRS ("we," "our," "us"). Your privacy is important to us, and we are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, CrowdHRS.com, and use our services.</p>
        
        <p class="intro-text">CrowdHRS is owned and operated by InspireDGT Ltd, a company registered in the United Kingdom.</p>
        
        <div class="privacy-section">
          <h2>1. Information We Collect</h2>
          <div class="subsection">
            <h3>a. Information You Provide</h3>
            <ul>
                <li>When you register as a user, recruiter, or company, we collect details such as your name, email address, phone number, and company information.</li>
                <li>Job applicants may upload their CVs and other career-related documents.</li>
                <li>Recruiters and users may submit referrals and associated information.</li>
                <li>Payment and billing details if you subscribe to paid services or receive referral rewards.</li>
                <li>Communication data, including messages sent via our platform.</li>
            </ul>
          </div>
          
          <div class="subsection">
            <h3>b. Information Collected Automatically</h3>
            <ul>
                <li>IP address, device information, browser type, and usage patterns.</li>
                <li>Cookies and tracking technologies to enhance user experience.</li>
                <li>Logs and metadata related to interactions with our services.</li>
            </ul>
          </div>
          
          <div class="subsection">
            <h3>c. Information from Third Parties</h3>
            <ul>
                <li>If you sign up using third-party authentication (e.g., Google, LinkedIn), we may collect basic profile information from those platforms.</li>
                <li>Payments are processed securely via third-party payment providers like Stripe and PayPal, and we do not store credit card details.</li>
                <li>Publicly available sources may be used to supplement user data where relevant.</li>
            </ul>
          </div>
        </div>
        
        <div class="privacy-section">
          <h2>2. How We Use Your Information</h2>
          <ul>
              <li>Provide, maintain, and improve our services.</li>
              <li>Facilitate job applications and referral-based recruitment processes.</li>
              <li>Process payments, including rewards for successful referrals.</li>
              <li>Enforce our terms of service and prevent fraudulent activity.</li>
              <li>Comply with legal and regulatory obligations.</li>
              <li>Communicate updates, notifications, and promotional content (you can opt out of marketing communications at any time).</li>
              <li>Conduct research, analytics, and statistical evaluations to enhance platform performance.</li>
          </ul>
        </div>
        
        <div class="privacy-section">
          <h2>3. How We Share Your Information</h2>
          <p>We do not sell your personal data. However, we may share your data under the following circumstances:</p>
          <ul>
              <li>With <strong>employers and recruiters</strong> to facilitate job applications and referrals.</li>
              <li>With <strong>third-party payment providers</strong> for processing transactions.</li>
              <li>With <strong>service providers</strong> who assist us with hosting, analytics, and customer support.</li>
              <li>If required by <strong>law enforcement or legal authorities</strong> to comply with legal obligations.</li>
              <li>In case of <strong>business transfers</strong>, such as a merger or acquisition.</li>
              <li>To enforce security, fraud prevention, or protect our rights and interests.</li>
          </ul>
        </div>
        
        <div class="privacy-section">
          <h2>4. Data Retention & Security</h2>
          <p>We retain your data for as long as necessary to fulfill the purposes outlined in this policy. We take reasonable security measures to protect your data, including encryption and access controls. However, no online system is 100% secure, and we encourage users to take their own precautions.</p>
        </div>
        
        <div class="privacy-section">
          <h2>5. Your Rights & Choices</h2>
          <ul>
              <li><strong>Access & Correction:</strong> You can request access to your personal data and update or correct inaccuracies.</li>
              <li><strong>Deletion:</strong> You can request the deletion of your account and associated data, subject to legal and contractual obligations.</li>
              <li><strong>Opt-out of Marketing:</strong> You can unsubscribe from marketing emails via the link in our emails.</li>
              <li><strong>Cookies & Tracking:</strong> You can manage cookie preferences through your browser settings.</li>
              <li><strong>Data Portability:</strong> You may request a copy of your personal data in a structured format where applicable.</li>
          </ul>
        </div>
        
        <div class="privacy-section">
          <h2>6. Cookies & Tracking Technologies</h2>
          <p>CrowdHRS uses cookies and similar tracking technologies to enhance functionality and user experience. You can control cookie settings through your browser.</p>
        </div>
        
        <div class="privacy-section">
          <h2>7. International Data Transfers</h2>
          <p>As a UK-based company, we store data within the United Kingdom and European Economic Area (EEA). If you access our services from outside these regions, your data may be transferred internationally in compliance with data protection laws.</p>
        </div>
        
        <div class="privacy-section">
          <h2>8. Updates to This Privacy Policy</h2>
          <p>We may update this Privacy Policy periodically to reflect changes in our services or legal obligations. The latest version will always be available on CrowdHRS.com.</p>
        </div>
        
        <div class="privacy-section">
          <h2>9. Contact Us</h2>
          <div class="contact-info">
            <p><strong>InspireDGT Ltd</strong></p>
            <p>338a Regents Park Road, Office 3 And 4,<br>
            London, England, N3 2LN</p>
            <p>Email: <a href="mailto:info@inspiredgt.co.uk" class="link">info@inspiredgt.co.uk</a></p>
            <p>Website: <a href="https://www.CrowdHRS.com" class="link">www.CrowdHRS.com</a></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Variables
$primary-color: #103660;
$accent-color: #fc6c2a;
$text-color: #333;
$light-gray: #f5f7fa;
$border-color: #eaeaea;
$link-color: #015cac;

.privacy-page-container {
  padding: 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 0.75rem;
  }
}

.privacy-container {
  max-width: 1000px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 8px;
  }
}

.title {
  color: $primary-color;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
}

.last-updated {
  color: #666;
  font-style: italic;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 0.9rem;
}

.intro-text {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: $text-color;
}

.privacy-content {
  > p {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    color: $text-color;
  }
}

.privacy-section {
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid $border-color;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  h2 {
    color: $primary-color;
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid $accent-color;
    display: inline-block;
  }
  
  h3 {
    color: $primary-color;
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
    line-height: 1.6;
    color: $text-color;
  }

  ul {
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      line-height: 1.6;
      color: $text-color;
      position: relative;
      
      &::marker {
        color: $accent-color;
      }
    }
  }
}

.subsection {
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.contact-info {
  background-color: $light-gray;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  
  p {
    margin-bottom: 0.75rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.link {
  color: $link-color;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: $accent-color;
    text-decoration: underline;
  }
}
</style> 